import {
  LockOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel, useIntl } from '@umijs/max';
import type { MenuProps } from 'antd';
import { App, Form, Input, Modal, Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { adminLogout } from '@/services/property_api/adminAuthController';
import { changePassword } from '@/services/property_api/sysUserController';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const intl = useIntl();
  const { currentUser } = initialState || {};
  // 优先从localStorage获取用户名，如果没有则使用currentUser中的信息
  const cachedUsername = localStorage.getItem('username');
  const displayName =
    cachedUsername ||
    currentUser?.name ||
    currentUser?.username ||
    intl.formatMessage({ id: 'component.avatarDropdown.user' });
  const firstLetter = displayName.charAt(0).toUpperCase();

  return (
    <span
      className="anticon"
      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
    >
      <div
        style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: '#f77d03',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 'bold',
        }}
      >
        {firstLetter}
      </div>
      {displayName}
    </span>
  );
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const { message } = App.useApp();
  const intl = useIntl();
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    await adminLogout();
    // 清除本地存储的token和用户名
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'changePassword') {
      setChangePasswordVisible(true);
      return;
    }
    history.push(`/account/${key}`);
  };

  const handleChangePassword = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const result = await changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });

      if (result.success) {
        message.success(intl.formatMessage({ id: 'component.avatarDropdown.changePassword.success' }));
        form.resetFields();
        setChangePasswordVisible(false);
        // 修改密码成功后，退出登录
        setTimeout(() => {
          flushSync(() => {
            setInitialState((s) => ({ ...s, currentUser: undefined }));
          });
          loginOut();
        }, 1000);
      } else {
        message.error(result.message || intl.formatMessage({ id: 'component.avatarDropdown.changePassword.failure' }));
      }
    } catch (error: any) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      console.error('修改密码失败:', error);
      message.error(error?.message || intl.formatMessage({ id: 'component.avatarDropdown.changePassword.failure' }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelChangePassword = () => {
    form.resetFields();
    setChangePasswordVisible(false);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser) {
    return loading;
  }

  const menuItems = [
    {
      key: 'changePassword',
      icon: <LockOutlined />,
      label: intl.formatMessage({ id: 'component.avatarDropdown.changePassword' }),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: 'component.avatarDropdown.logout' }),
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>

      {/* 修改密码弹窗 */}
      <Modal
        title={intl.formatMessage({ id: 'component.avatarDropdown.changePassword.title' })}
        open={changePasswordVisible}
        onOk={handleChangePassword}
        onCancel={handleCancelChangePassword}
        confirmLoading={submitting}
        okText={intl.formatMessage({ id: 'component.avatarDropdown.changePassword.ok' })}
        cancelText={intl.formatMessage({ id: 'component.avatarDropdown.changePassword.cancel' })}
        width={500}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="oldPassword"
            label={intl.formatMessage({ id: 'component.avatarDropdown.changePassword.oldPassword.label' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'component.avatarDropdown.changePassword.oldPassword.required' }),
              },
              {
                min: 6,
                message: intl.formatMessage({ id: 'component.avatarDropdown.changePassword.oldPassword.min' }),
              },
            ]}
          >
            <Input.Password
              placeholder={intl.formatMessage({ id: 'component.avatarDropdown.changePassword.oldPassword.placeholder' })}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={intl.formatMessage({ id: 'component.avatarDropdown.changePassword.newPassword.label' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'component.avatarDropdown.changePassword.newPassword.required' }),
              },
              {
                min: 6,
                max: 20,
                message: intl.formatMessage({ id: 'component.avatarDropdown.changePassword.newPassword.length' }),
              },
            ]}
          >
            <Input.Password
              placeholder={intl.formatMessage({ id: 'component.avatarDropdown.changePassword.newPassword.placeholder' })}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={intl.formatMessage({ id: 'component.avatarDropdown.changePassword.confirmPassword.label' })}
            dependencies={['newPassword']}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'component.avatarDropdown.changePassword.confirmPassword.required' }),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(intl.formatMessage({ id: 'component.avatarDropdown.changePassword.confirmPassword.mismatch' })),
                  );
                },
              }),
            ]}
          >
            <Input.Password
              placeholder={intl.formatMessage({ id: 'component.avatarDropdown.changePassword.confirmPassword.placeholder' })}
              autoComplete="off"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
